import React, { useState, useEffect, useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface Comment {
  id: number;
  body: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    full_name: string;
    email: string;
  };
}

interface VendorInvoiceNotesModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  vendorName?: string;
  onNotesChanged?: () => void;
}

const VendorInvoiceNotesModal: React.FC<VendorInvoiceNotesModalProps> = ({
  open,
  onClose,
  invoiceId,
  vendorName,
  onNotesChanged,
}) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/vendors/vendor_invoices/${invoiceId}/comments`);
      setComments(response.data || []);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch notes';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newNote.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`/vendors/vendor_invoices/${invoiceId}/comments`, {
        comment: { body: newNote.trim() },
      });
      setNewNote('');
      fetchComments();
      onNotesChanged?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to add note';
      showErrorSnackBar(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: number) => {
    try {
      await axios.delete(`/vendors/vendor_invoices/${invoiceId}/comments/${commentId}`);
      showSuccessSnackBar('Note deleted');
      fetchComments();
      onNotesChanged?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to delete note';
      showErrorSnackBar(errorMessage);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    if (open) {
      fetchComments();
      setNewNote('');
    }
  }, [open, invoiceId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Notes</Typography>
            {vendorName && (
              <Typography variant="caption" color="text.secondary">
                {vendorName}
              </Typography>
            )}
          </Box>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : comments.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No notes yet. Add one below.
            </Typography>
          </Box>
        ) : (
          <Box>
            {comments.map((comment, index) => (
              <Box key={comment.id}>
                <Box py={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle2">{comment.user?.full_name || 'Unknown User'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.created_at).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {comment.body}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete note">
                      <IconButton size="small" onClick={() => handleDelete(comment.id)} sx={{ ml: 1 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {index < comments.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <TextField
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note about this invoice..."
          fullWidth
          multiline
          maxRows={3}
          size="small"
          disabled={submitting}
        />
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!newNote.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ ml: 1, minWidth: 90 }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorInvoiceNotesModal;
